import type { CSSProperties } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { FormattedMessage } from 'react-intl';

import { useClientProfile } from '../hooks/client/useClientProfile';
import PaymentDialog from '../components/dialogs/PaymentDialog';

const sectionCard: CSSProperties = {
  background: 'var(--ios-card-bg)',
  borderRadius: 'var(--ios-radius-xl)',
  border: '1px solid var(--ios-card-border)',
  boxShadow: 'var(--ios-card-shadow)',
  padding: '1rem',
  marginBottom: '0.75rem'
};

const infoRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.55rem 0',
  borderBottom: '1px solid var(--ios-divider)',
  fontSize: '0.88rem',
  gap: '0.5rem'
};

const infoLabel: CSSProperties = {
  fontWeight: 600,
  color: 'var(--ios-text-secondary)',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em'
};

const infoValue: CSSProperties = {
  color: 'var(--ios-text)',
  fontWeight: 500,
  textAlign: 'right'
};

const sectionTitle: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: 'var(--ios-text)',
  margin: '0 0 0.75rem',
  letterSpacing: '-0.01em',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

const iconBox = (bg: string, color: string): CSSProperties => ({
  width: '2.2rem',
  height: '2.2rem',
  borderRadius: '12px',
  background: bg,
  color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

const pillStyle = (bg: string, color: string): CSSProperties => ({
  padding: '0.2rem 0.6rem',
  borderRadius: 'var(--ios-radius-pill)',
  background: bg,
  color,
  fontSize: '0.72rem',
  fontWeight: 600
});

export default function ClientProfile() {
  const {
    intl,
    client,
    toast,
    personalInfo,
    subscription,
    streak,
    todayTraining,
    recentHistory,
    daysUntilExpiry,
    isPerSession,
    sessionsUsed,
    subscriptionStatusSeverity,
    editDialogVisible,
    editName,
    setEditName,
    editBirthdate,
    setEditBirthdate,
    editGender,
    setEditGender,
    editPhoneNumber,
    setEditPhoneNumber,
    fitnessGoal,
    setFitnessGoal,
    activityLevel,
    setActivityLevel,
    isPaymentDialogVisible,
    handleEditPersonalInfo,
    handleEditDialogClose,
    handleSavePersonalInfo,
    handleOpenPaymentDialog,
    handlePaymentDialogClose,
    handleStartTraining,
    handleViewTodayDetails,
    isTodayCompleted,
    handleContactCoach,
    fitnessGoalOptions,
    activityLevelOptions,
    genderOptions,
    formatDate,
    getSeverity
  } = useClientProfile();

  const dailyStreak = streak.daily?.currentStreak ?? 0;
  const coach = client?.coach;

  return (
    <div style={{ padding: '0.75rem', maxWidth: '900px', margin: '0 auto' }}>
      <Toast ref={toast} />

      <h1
        style={{
          fontSize: 'clamp(1.35rem, 3vw, 1.75rem)',
          fontWeight: 800,
          letterSpacing: '-0.03em',
          margin: '0 0 1rem',
          color: 'var(--ios-text)'
        }}
      >
        <FormattedMessage id="profile.title.mine" />
      </h1>

      <div className="grid">
        {/* Column 1 */}
        <div className="col-12 md:col-6">
          {/* Today card */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>
              <span style={iconBox('rgba(163, 230, 53, 0.18)', '#4d7c0f')}>
                <i className="pi pi-calendar" />
              </span>
              <FormattedMessage id="profile.today.title" />
            </h3>

            {todayTraining ? (
              isTodayCompleted ? (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      marginBottom: '0.6rem'
                    }}
                  >
                    <span style={iconBox('rgba(34,197,94,0.12)', '#22c55e')}>
                      <i className="pi pi-check-circle" style={{ fontSize: '1.1rem' }} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          color: 'var(--ios-text)',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        <FormattedMessage id="profile.today.completed.title" />
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.8rem',
                          color: 'var(--ios-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {todayTraining.planName}
                      </p>
                    </div>
                  </div>
                  <Button
                    label={intl.formatMessage({ id: 'profile.today.viewDetails' })}
                    icon="pi pi-eye"
                    onClick={handleViewTodayDetails}
                    className="w-full"
                    style={{
                      background: 'rgba(163, 230, 53, 0.18)',
                      color: '#4d7c0f',
                      border: 'none',
                      borderRadius: 'var(--ios-radius-md)',
                      fontWeight: 600,
                      padding: '0.6rem',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>
              ) : (
                <div>
                  <p
                    style={{
                      margin: '0 0 0.35rem',
                      fontSize: '0.78rem',
                      color: 'var(--ios-text-secondary)',
                      fontWeight: 500
                    }}
                  >
                    <FormattedMessage id="profile.today.training" />
                  </p>
                  <p
                    style={{
                      margin: '0 0 0.85rem',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      color: 'var(--ios-text)',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {todayTraining.planName}
                  </p>
                  <Button
                    label={intl.formatMessage({ id: 'profile.today.startTraining' })}
                    icon="pi pi-play"
                    onClick={handleStartTraining}
                    className="w-full"
                    style={{
                      background: '#22c55e',
                      border: 'none',
                      borderRadius: 'var(--ios-radius-md)',
                      fontWeight: 600,
                      padding: '0.7rem',
                      fontSize: '0.92rem',
                      boxShadow: '0 4px 14px rgba(34,197,94,0.25)'
                    }}
                  />
                </div>
              )
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={iconBox('rgba(245,158,11,0.1)', '#f59e0b')}>
                  <i className="pi pi-moon" />
                </span>
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: 'var(--ios-text)'
                    }}
                  >
                    <FormattedMessage id="profile.today.restDay" />
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.8rem',
                      color: 'var(--ios-text-secondary)'
                    }}
                  >
                    <FormattedMessage id="profile.today.restSubtitle" />
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Streak card */}
          <div style={sectionCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
              <span style={iconBox('rgba(245,158,11,0.12)', '#f59e0b')}>
                <i className="pi pi-bolt" style={{ fontSize: '1.1rem' }} />
              </span>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    fontWeight: 600,
                    color: 'var(--ios-text-secondary)'
                  }}
                >
                  <FormattedMessage id="profile.streak.title" />
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '1.8rem',
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: 'var(--ios-text)'
                  }}
                >
                  {dailyStreak}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.8rem',
                    color: 'var(--ios-text-secondary)'
                  }}
                >
                  {dailyStreak > 0 ? (
                    <FormattedMessage id="profile.streak.days" />
                  ) : (
                    <FormattedMessage id="profile.streak.startNow" />
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Subscription card */}
          {subscription?.coachPlan ? (
            <div style={sectionCard}>
              <h3 style={sectionTitle}>
                <span style={iconBox('rgba(34,197,94,0.1)', '#22c55e')}>
                  <i className="pi pi-credit-card" />
                </span>
                <FormattedMessage id="profile.subscription" />
              </h3>

              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.subscription.planName" />
                </span>
                <span style={infoValue}>{subscription.coachPlan.name}</span>
              </div>
              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.subscription.frequency" />
                </span>
                <span style={infoValue}>
                  <FormattedMessage
                    id={`profile.subscription.frequency.${subscription.coachPlan.paymentFrequency ?? 'monthly'}`}
                  />
                </span>
              </div>
              {isPerSession ? (
                <div style={infoRow}>
                  <span style={infoLabel}>
                    <FormattedMessage id="profile.subscription.sessionsUsed" />
                  </span>
                  <span style={infoValue}>{sessionsUsed}</span>
                </div>
              ) : (
                subscription.subscription?.endDate && (
                  <div style={infoRow}>
                    <span style={infoLabel}>
                      <FormattedMessage id="profile.subscription.endDate" />
                    </span>
                    <span style={infoValue}>
                      {formatDate(subscription.subscription.endDate)}
                      {daysUntilExpiry !== null && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            ...pillStyle(
                              daysUntilExpiry < 0
                                ? 'rgba(239,68,68,0.1)'
                                : daysUntilExpiry <= 7
                                  ? 'rgba(245,158,11,0.12)'
                                  : 'rgba(34,197,94,0.1)',
                              daysUntilExpiry < 0 ? '#ef4444' : daysUntilExpiry <= 7 ? '#f59e0b' : '#22c55e'
                            )
                          }}
                        >
                          {daysUntilExpiry < 0 ? (
                            <FormattedMessage id="profile.subscription.expired" />
                          ) : (
                            <FormattedMessage id="profile.subscription.daysLeft" values={{ days: daysUntilExpiry }} />
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                )
              )}
              <div style={{ ...infoRow, borderBottom: 'none' }}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.subscription.status" />
                </span>
                <Tag
                  value={subscription.subscription?.status || ''}
                  severity={subscriptionStatusSeverity}
                  style={{
                    borderRadius: 'var(--ios-radius-pill)',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                />
              </div>

              <Button
                label={intl.formatMessage({ id: 'payment.makePayment' })}
                icon="pi pi-credit-card"
                onClick={handleOpenPaymentDialog}
                className="w-full mt-3"
                style={{
                  background: '#22c55e',
                  border: 'none',
                  borderRadius: 'var(--ios-radius-md)',
                  fontWeight: 600,
                  padding: '0.6rem',
                  fontSize: '0.88rem'
                }}
              />
            </div>
          ) : (
            <div
              style={{
                ...sectionCard,
                background: 'var(--ios-surface-subtle)',
                textAlign: 'center',
                padding: '1.25rem'
              }}
            >
              <i
                className="pi pi-inbox"
                style={{ fontSize: '1.5rem', color: 'var(--ios-text-tertiary)', marginBottom: '0.4rem' }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: '0.88rem',
                  color: 'var(--ios-text-secondary)',
                  fontWeight: 500
                }}
              >
                <FormattedMessage id="profile.subscription.none" />
              </p>
            </div>
          )}
        </div>

        {/* Column 2 */}
        <div className="col-12 md:col-6">
          {/* Coach card */}
          {coach && (
            <div style={sectionCard}>
              <h3 style={sectionTitle}>
                <span style={iconBox('rgba(163, 230, 53, 0.18)', '#4d7c0f')}>
                  <i className="pi pi-user" />
                </span>
                <FormattedMessage id="profile.coach.title" />
              </h3>
              {coach.name && (
                <div style={infoRow}>
                  <span style={infoLabel}>
                    <FormattedMessage id="profile.name" />
                  </span>
                  <span style={infoValue}>{coach.name}</span>
                </div>
              )}
              {coach.email && (
                <div style={{ ...infoRow, borderBottom: coach.phone ? '1px solid var(--ios-divider)' : 'none' }}>
                  <span style={infoLabel}>
                    <FormattedMessage id="profile.email" />
                  </span>
                  <span style={{ ...infoValue, fontSize: '0.8rem', wordBreak: 'break-all' }}>{coach.email}</span>
                </div>
              )}
              {coach.phone && (
                <div style={{ ...infoRow, borderBottom: 'none' }}>
                  <span style={infoLabel}>
                    <FormattedMessage id="profile.phone" />
                  </span>
                  <span style={infoValue}>{coach.phone}</span>
                </div>
              )}
              {coach.email && (
                <Button
                  label={intl.formatMessage({ id: 'profile.coach.contact' })}
                  icon="pi pi-envelope"
                  onClick={handleContactCoach}
                  className="w-full mt-3"
                  style={{
                    background: 'rgba(163, 230, 53, 0.18)',
                    color: '#4d7c0f',
                    border: 'none',
                    borderRadius: 'var(--ios-radius-md)',
                    fontWeight: 600,
                    padding: '0.6rem',
                    fontSize: '0.88rem'
                  }}
                />
              )}
            </div>
          )}

          {/* Personal Info */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>
              <span style={iconBox('rgba(163, 230, 53, 0.18)', '#4d7c0f')}>
                <i className="pi pi-id-card" />
              </span>
              <FormattedMessage id="profile.personalInfo" />
            </h3>

            {personalInfo?.name && (
              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.name" />
                </span>
                <span style={infoValue}>{personalInfo.name}</span>
              </div>
            )}
            {personalInfo?.user?.email && (
              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.email" />
                </span>
                <span style={{ ...infoValue, fontSize: '0.8rem', wordBreak: 'break-all' }}>
                  {personalInfo.user.email}
                </span>
              </div>
            )}
            {personalInfo?.birthdate && (
              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.birthdate" />
                </span>
                <span style={infoValue}>{formatDate(personalInfo.birthdate)}</span>
              </div>
            )}
            {personalInfo?.gender && (
              <div style={infoRow}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.gender" />
                </span>
                <span style={infoValue}>{personalInfo.gender}</span>
              </div>
            )}
            {personalInfo?.phoneNumber != null && (
              <div style={{ ...infoRow, borderBottom: 'none' }}>
                <span style={infoLabel}>
                  <FormattedMessage id="profile.phone" />
                </span>
                <span style={infoValue}>{personalInfo.phoneNumber}</span>
              </div>
            )}

            <Button
              label={intl.formatMessage({ id: 'profile.edit' })}
              icon="pi pi-pencil"
              className="w-full mt-3"
              onClick={handleEditPersonalInfo}
              style={{
                background: 'rgba(163, 230, 53, 0.18)',
                color: '#4d7c0f',
                border: 'none',
                borderRadius: 'var(--ios-radius-md)',
                fontWeight: 600,
                fontSize: '0.88rem',
                padding: '0.6rem'
              }}
            />
          </div>

          {/* Recent history */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>
              <span style={iconBox('rgba(163, 230, 53, 0.18)', '#4d7c0f')}>
                <i className="pi pi-history" />
              </span>
              <FormattedMessage id="profile.recentHistory" />
            </h3>

            {recentHistory.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  color: 'var(--ios-text-secondary)',
                  textAlign: 'center',
                  padding: '0.75rem 0'
                }}
              >
                <FormattedMessage id="profile.recentHistory.empty" />
              </p>
            ) : (
              <div className="flex flex-column gap-2">
                {recentHistory.map((session, idx) => (
                  <div
                    key={session.id ?? idx}
                    className="flex align-items-center justify-content-between"
                    style={{
                      padding: '0.6rem 0.75rem',
                      background: 'var(--ios-surface-subtle)',
                      borderRadius: 'var(--ios-radius-md)'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.88rem',
                          fontWeight: 600,
                          color: 'var(--ios-text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {session.workout?.planName ?? '—'}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.75rem',
                          color: 'var(--ios-text-secondary)'
                        }}
                      >
                        {session.trainingSession?.sessionDate
                          ? formatDate(new Date(session.trainingSession.sessionDate))
                          : '—'}
                      </p>
                    </div>
                    <Tag
                      value={session.status}
                      severity={getSeverity(session.status)}
                      style={{
                        borderRadius: 'var(--ios-radius-pill)',
                        fontSize: '0.68rem',
                        fontWeight: 600
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user-edit" style={{ color: '#4d7c0f' }} />
            <span style={{ fontWeight: 700 }}>{intl.formatMessage({ id: 'profile.dialog.edit' })}</span>
          </div>
        }
        visible={editDialogVisible}
        style={{ width: 'min(92vw, 480px)' }}
        onHide={handleEditDialogClose}
        draggable={false}
        resizable={false}
        dismissableMask
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label={intl.formatMessage({ id: 'profile.dialog.cancel' })}
              icon="pi pi-times"
              onClick={handleEditDialogClose}
              className="p-button-text"
              style={{ color: 'var(--ios-text-secondary)', fontSize: '0.85rem' }}
            />
            <Button
              label={intl.formatMessage({ id: 'profile.dialog.save' })}
              icon="pi pi-check"
              onClick={handleSavePersonalInfo}
              autoFocus
              style={{
                background: '#a3e635',
                border: 'none',
                borderRadius: 'var(--ios-radius-md)',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3" style={{ padding: '0.25rem 0' }}>
          <div>
            <label htmlFor="editName" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.name" />
            </label>
            <InputText
              id="editName"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>

          <div>
            <label htmlFor="editBirthdate" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.birthdate" />
            </label>
            <Calendar
              id="editBirthdate"
              value={editBirthdate}
              onChange={(e) => setEditBirthdate((e.value as Date | null) ?? null)}
              dateFormat="dd/mm/yy"
              showIcon
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>

          <div>
            <label htmlFor="editGender" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.gender" />
            </label>
            <Dropdown
              id="editGender"
              options={genderOptions}
              value={editGender}
              onChange={(e) => setEditGender(e.value)}
              placeholder={intl.formatMessage({ id: 'common.select' })}
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>

          <div>
            <label htmlFor="fitnessGoal" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.dialog.fitnessGoal" />
            </label>
            <MultiSelect
              id="fitnessGoal"
              options={fitnessGoalOptions}
              value={fitnessGoal}
              onChange={(e) => setFitnessGoal(e.value)}
              placeholder={intl.formatMessage({ id: 'common.select' })}
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>

          <div>
            <label htmlFor="activityLevel" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.dialog.activityLevel" />
            </label>
            <Dropdown
              id="activityLevel"
              options={activityLevelOptions}
              value={activityLevel}
              onChange={(e) => setActivityLevel(e.value)}
              placeholder={intl.formatMessage({ id: 'common.select' })}
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>

          <div>
            <label htmlFor="editPhone" style={{ ...infoLabel, display: 'block', marginBottom: '0.3rem' }}>
              <FormattedMessage id="profile.phone" />
            </label>
            <InputNumber
              id="editPhone"
              maxLength={11}
              useGrouping={false}
              value={editPhoneNumber}
              onChange={(e) => setEditPhoneNumber(e.value)}
              className="w-full"
              style={{ borderRadius: 'var(--ios-radius-md)' }}
            />
          </div>
        </div>
      </Dialog>

      <PaymentDialog
        visible={isPaymentDialogVisible}
        onHide={handlePaymentDialogClose}
        subscription={subscription}
        coachId={client?.coach?.id as number}
      />
    </div>
  );
}
